import React, { useState, useEffect, useRef } from 'react';
import { FaMotorcycle, FaBusAlt } from 'react-icons/fa';
import { MdOutlineLocalTaxi } from 'react-icons/md';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import './Hero.css';

const containerStyle = {
  width: '100%',
  height: '100%',
};

const center = {
  lat: 7.3775,
  lng: 3.9470,
};

const Hero = () => {
  const [formData, setFormData] = useState({
    pickup: '',
    drop: '',
    date: '',
    hour: '',
    minutes: '',
    ampm: 'AM',
    tripType: 'single',
    vehicleType: 'lux',
  });

  const [showModal, setShowModal] = useState(false);

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: 'AIzaSyB3bI4CNXdnrHAhzW1HJNc-IxZGWdSry90',
    libraries: ['places'],
  });

  const pickupRef = useRef(null);
  const dropRef = useRef(null);

  useEffect(() => {
    const currentDate = new Date().toISOString().split('T')[0];
    setFormData((prevData) => ({ ...prevData, date: currentDate }));

    if (isLoaded && window.google && window.google.maps) {
      const options = {
        types: ['geocode'],
      };

      const pickupInput = pickupRef.current;
      const dropInput = dropRef.current;

      if (pickupInput && dropInput) {
        const pickupAutocomplete = new window.google.maps.places.Autocomplete(pickupInput, options);
        pickupAutocomplete.addListener('place_changed', () => {
          const place = pickupAutocomplete.getPlace();
          if (place && place.formatted_address) {
            setFormData((prevData) => ({ ...prevData, pickup: place.formatted_address }));
          }
        });

        const dropAutocomplete = new window.google.maps.places.Autocomplete(dropInput, options);
        dropAutocomplete.addListener('place_changed', () => {
          const place = dropAutocomplete.getPlace();
          if (place && place.formatted_address) {
            setFormData((prevData) => ({ ...prevData, drop: place.formatted_address }));
          }
        });
      }
    }
  }, [isLoaded]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleTripChange = (e) => {
    setFormData({ ...formData, tripType: e.target.value });
  };

  const handleVehicleSelect = (type) => {
    setFormData({ ...formData, vehicleType: type });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const { pickup, drop, date, hour, minutes } = formData;
    if (!pickup || !drop || !date || !hour || !minutes) {
      alert("Please fill all required fields before proceeding.");
      return;
    }

    setShowModal(true);

    setTimeout(() => {
      window.location.href = 'https://www.wakaforme.com/booking/login.php';
    }, 2000);
  };

  if (loadError) {
    return <div>Error loading Google Maps API.</div>;
  }

  return (
    <div className="hero-container relative">
      <div className="form-section">
        <h2 className="form-title">Book Your Ride Now</h2>

        <div className="vehicle-buttons">
          <button onClick={() => handleVehicleSelect('bike')} className={`vehicle-button ${formData.vehicleType === 'bike' ? 'selected' : ''}`}>
            <FaMotorcycle size={30} />
            <span className="font-montserrat">Bike</span>
          </button>
          <button onClick={() => handleVehicleSelect('lux')} className={`vehicle-button ${formData.vehicleType === 'lux' ? 'selected' : ''}`}>
            <MdOutlineLocalTaxi size={30} />
            <span className="font-montserrat">LUX</span>
          </button>
          <button onClick={() => handleVehicleSelect('bus')} className={`vehicle-button ${formData.vehicleType === 'bus' ? 'selected' : ''}`}>
            <FaBusAlt size={30} />
            <span className="font-montserrat">Bus</span>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="ride-form">
          <label>
            <span className="font-montserrat">Pickup Place :</span>
            <input ref={pickupRef} type="text" name="pickup" value={formData.pickup} onChange={handleChange} placeholder="Pickup Location Address" required />
          </label>

          <label>
            <span className="font-montserrat">Drop Place :</span>
            <input ref={dropRef} type="text" name="drop" value={formData.drop} onChange={handleChange} placeholder="Drop off Location Address" required />
          </label>

          <label>
            <span className="font-montserrat">Pickup Date :</span>
            <input type="date" name="date" value={formData.date} onChange={handleChange} required />
          </label>

          <div className="time-inputs">
            <label>
              <span className="font-montserrat">Hour:</span>
              <input type="number" name="hour" min="1" max="12" value={formData.hour} onChange={handleChange} required />
            </label>
            <label>
              <span className="font-montserrat">Minutes:</span>
              <input type="number" name="minutes" min="0" max="59" value={formData.minutes} onChange={handleChange} required />
            </label>
            <label>
              <span className="font-montserrat">AM/PM:</span>
              <select name="ampm" value={formData.ampm} onChange={handleChange}>
                <option>AM</option>
                <option>PM</option>
              </select>
            </label>
          </div>

          <div className="trip-type">
            <label>
              <input type="radio" name="tripType" value="single" checked={formData.tripType === 'single'} onChange={handleTripChange} />
              <span className="font-montserrat">Single Trip</span>
            </label>
            <label>
              <input type="radio" name="tripType" value="round" checked={formData.tripType === 'round'} onChange={handleTripChange} />
              <span className="font-montserrat">Round Trip</span>
            </label>
          </div>

          <button type="submit" className="submit-btn font-montserrat">Get Started</button>
        </form>
      </div>

      <div className="map-section">
        {isLoaded ? (
          <GoogleMap mapContainerStyle={containerStyle} center={center} zoom={13}>
            <Marker position={center} />
          </GoogleMap>
        ) : (
          <div className="loading">Loading map...</div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 animate-fadeIn">
          <div className="bg-white p-8 rounded-lg shadow-lg text-center animate-bounceIn">
            <h3 className="text-lg font-semibold font-montserrat">Redirecting to booking page...</h3>
            <p className="text-sm mt-2 font-montserrat">Please wait while we take you to the next step.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Hero;
